import { connect, ConnectedProps } from 'react-redux'
import { TwinTableLink } from '../../../../../baseComponents/TwinTable'
import { TableComponentBaseScreenInnerProps } from '../../../../../baseComponents/TwinTable/BaseScreenTableInner/types'
import { TwinDictionary } from '../../../../../utils/globals/dictionary'
import useTwinTranslation from '../../../../../utils/hooks/useTwinTranslation'
import { SellTagsPayload } from '../../../../../utils/reducers/sells/sellTags'
import { AllReduxPayloads } from '../../../../../utils/reducers'
import withLoading from '../../../../../utils/hoc/withLoading'
import { ColumnTableSchema } from '../../../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import { DisplayPriceGettingParams } from '../../../../../baseComponents/Displays/DisplayPrice'
import { CustomRenderDate, CustomRenderString, CustomRenderStringAndColorNull } from '../../../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import { RenderSellInvoiceStatus } from '../../../../../app/erp/screens/Logistic/QuoteParent/subcomponents'
import { RenderSellTags } from '../../../../sells/CustomRenderTable'
import locations from '../../../../../utils/globals/locations'
import { SellInvoiceModelType } from '@teinor/erp/types/company/order/sell/sellInvoice'

type TableCustomerInvoicesProps = TableComponentBaseScreenInnerProps & ReduxTableCustomerInvoices & {}

const TableCustomerInvoices: React.FC<TableCustomerInvoicesProps> = ({ sellTags, setOpened, setTableInstance, extraWhereParams, ...rest }) => {
    const { t } = useTwinTranslation()
    const sellInvoiceStatusComplex: TwinDictionary = {
        0: { name: t('draft', 'Borrador'), id: 0 },
        1: { name: t('pendingInvoiced', 'Factura pte.'), id: 1 },
        3: { name: t('finished', 'Finalizado'), id: 3 },
    }
    const columns: ColumnTableSchema<SellInvoiceModelType> = [
        {
            id: 'full_assigned_number',
            dataKey: 'full_assigned_number',
            label: t('invoice', 'Factura'),
            sortable: true,
            width: 120,
            customRender: (parameterValue, allRowData) => <CustomRenderString value={parameterValue} className={allRowData?.assigned_number ? '' : 'text-red-BA'} />,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('invoice', 'Factura') }
            },
        },
        {
            id: 'custom_name',
            dataKey: 'custom_name',
            label: t('customNameShort', 'Nombre pers.'),
            sortable: true,
            minWidth: 130,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('customName', 'Nombre personalizado'), }
            },
            hideColumnByDefault: true
        },
        {
            id: 'status',
            dataKey: 'status',
            label: t('status', 'Estado'),
            minWidth: 140,
            sortable: true,
            customRender: (parameterValue) => <RenderSellInvoiceStatus value={parameterValue} />,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelect',
                extraComponentData: { items: sellInvoiceStatusComplex, label: t('statusInvoice', 'Estado del albarán') }
            },
        },
        {
            id: 'WorkingCenterId',
            dataKey: 'WorkingCenterId',
            searchKey: 'WorkingCenter.name',
            minWidth: 115,
            label: t('workingCenter', 'Centro de trabajo'),
            customRender: (_parameterValue, allRowData) => <CustomRenderString value={allRowData?.WorkingCenter?.name || ''} />,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('workingCenter', 'Centro de trabajo') }
            },
        },
        {
            id: 'processed_date',
            dataKey: 'processed_date',
            label: t('createdDate', 'Fecha de creación'),
            minWidth: 165,
            customRender: (parameterValue, allRowData) => <CustomRenderDate value={parameterValue || allRowData.createdAt} className={parameterValue ? '' : 'text-red-BA'} />,
            sortable: true,
        },
        {
            id: 'total_total',
            dataKey: 'total_total',
            label: t('total', 'Total'),
            sortable: true,
            minWidth: 100,
            customRender: (parameterValue, allRowData) => <DisplayPriceGettingParams value={parseFloat(parameterValue)} with_vat={allRowData.with_vat} CurrencyId={allRowData.CurrencyId} />,
        },
        {
            id: 'CustomerProjectId',
            dataKey: 'CustomerProjectId',
            label: t('project', 'Proyecto'),
            hideColumnByDefault: true,
            customRender: (_parameterValue, allRowData) => <CustomRenderString value={allRowData?.CustomerProject?.name || ''} />
        },
        {
            id: 'CustomerProjectType',
            dataKey: 'id',
            hideColumnByDefault: true,
            label: t('projectType', 'Tipo de proyecto'),
            searchKey: 'CustomerProject.CustomerProjectType.name',
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('projectType', 'Tipo de proyecto') }
            },
            customRender: (parameterValue, allRowData) => <CustomRenderString value={allRowData?.CustomerProject?.CustomerProjectType?.name} />
        },
        {
            id: 'CurrentCProjectStatusId',
            dataKey: 'id',
            label: t('projectStatusShort', 'Estado proy.'),
            customRender: (_parameterValue, allRowData) => <CustomRenderStringAndColorNull value={allRowData?.CustomerProject?.CurrentCProjectStatus?.name} color={allRowData?.CustomerProject?.CurrentCProjectStatus?.color} />,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('status', 'Estado') }
            },
            searchKey: 'CustomerProject.CurrentCProjectStatus.name',
            sortable: true,
            sortKey: 'CustomerProject.CurrentCProjectStatus.order',
            minWidth: 150
        },
        {
            id: 'CampaignCustomerId',
            dataKey: 'CampaignCustomerId',
            label: t('campaign', 'Campaña'),
            hideColumnByDefault: true,
            customRender: (_parameterValue, allRowData) => <CustomRenderString value={allRowData?.CampaignCustomer?.Campaign?.name || ''} />,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('campaign', 'Campaña') }
            },
            searchKey: 'CampaignCustomer.Campaign.name',
            sortable: true,
            sortKey: 'CampaignCustomer.Campaign.name',
            minWidth: 130
        },
        {
            id: 'CurrentCCampaignStatusId',
            dataKey: 'id',
            label: t('campaignStatusShort', 'Estado camp.'),
            customRender: (_parameterValue, allRowData) => <CustomRenderStringAndColorNull value={allRowData?.CampaignCustomer?.CurrentCampaignStatus?.name} color={allRowData?.CampaignCustomer?.CurrentCampaignStatus?.color} />,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('status', 'Estado') }
            },
            searchKey: 'CampaignCustomer.CurrentCampaignStatus.name',
            sortable: true,
            sortKey: 'CampaignCustomer.CurrentCampaignStatus.order',
            minWidth: 160
        },
        {
            id: 'finished_date',
            dataKey: 'finished_date',
            label: t('finishedDateLittleShort', 'Fecha finalización'),
            hideColumnByDefault: true,
            customRender: (parameterValue, allRowData) => <CustomRenderDate value={parameterValue} />,
            sortable: true,
            minWidth: 165
        },
        {
            id: 'OrderTags',
            dataKey: 'OrderTags',
            label: t('tags', 'Etiquetas'),
            customRender: (_parameterValue, allRowData) => <RenderSellTags {...{ allRowData, sellTags }} />,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSearchBar',
                extraComponentData: { items: sellTags?.sell || {}, label: t('tags', 'Etiquetas') }
            },
            generalSearchable: false,
            sortable: false,
        },
    ]
    return (
        <TwinTableLink createRowDataLink={[locations.sellInvoiceSingle, ['$id']]} columns={columns} name='customersInvoices' getDataFrom='/api/app/order/sell/sellInvoice/getAllSellInvoicesComplete'
            onRowClick={(_id, rowData) => setOpened({
                type: 'edit',
                allRowData: rowData,
            })} setTableInstance={setTableInstance} extraWhereParams={extraWhereParams} {...rest}
        >
        </TwinTableLink>
    )
}

const TableCustomerInvoicesDispatch = {
    setTags: (payload: SellTagsPayload) => ({ type: 'CHANGE_SELL_TAGS', payload }),
}

export type ReduxTableCustomerInvoices = ConnectedProps<typeof tableCustomerInvoicesConnect>
const mapTableCustomerInvoicesConnector = (state: AllReduxPayloads) => ({ sellTags: state.sellTags })
const tableCustomerInvoicesConnect = connect(mapTableCustomerInvoicesConnector, TableCustomerInvoicesDispatch)
const TableCustomerInvoicesConnectLoading = withLoading(TableCustomerInvoices, [{ fetchUrl: '/api/app/order/sell/sellTag/getAllSellTags', propName: 'sellTags', setFunctionName: 'setTags' }])
const TableCustomerInvoicesConnect = tableCustomerInvoicesConnect(TableCustomerInvoicesConnectLoading)

export default TableCustomerInvoicesConnect